import type { IntlShape } from "@formatjs/intl";
import { Flex, Typography } from "antd";
import type { KeyboardEvent } from "react";
import { formatRelativeTime } from "../utils/formatRelativeTime";
import type { NotificationItem, NotificationRecipient } from "../utils/useNotifications";
import styles from "./CommentInboxItem.module.css";
import { getNotificationTypeConfig, resolveIconColors } from "./notificationTypeConfig";

const REACTION_NOTIFICATION_TYPE = "REACTION_ADDED";

function getSenderName(sender: NotificationRecipient | undefined) {
  if (!sender) return "";
  const fullName = [sender.first_name, sender.last_name].filter(Boolean).join(" ");
  return fullName || sender.username;
}

export function CommentInboxItem({
  notification,
  intl,
  onOpen,
}: {
  notification: NotificationItem;
  intl: IntlShape;
  onOpen: (notification: NotificationItem) => void;
}) {
  const typeConfig = getNotificationTypeConfig(notification.notification_type);
  const colors = resolveIconColors(typeConfig.color);
  const isUnread = !notification.read_at;
  const isReaction = notification.notification_type === REACTION_NOTIFICATION_TYPE;
  const commentPreview = notification.comment?.content_preview;
  const ticketNumber = notification.ticket?.ticket_number;
  const taskNumber = notification.ticket_item?.ticket_item_number;

  const contextLabel = [
    ticketNumber != null &&
      intl.formatMessage({ id: "comment_inbox_work_order" }, { number: ticketNumber }),
    taskNumber != null &&
      intl.formatMessage({ id: "comment_inbox_task" }, { number: taskNumber }),
  ]
    .filter(Boolean)
    .join(" · ");

  const openOnKeyboardActivation = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(notification);
    }
  };

  return (
    <Flex
      role="button"
      tabIndex={0}
      className={`${styles.inboxItem} ${isUnread ? styles.inboxItemUnread : ""}`}
      onClick={() => onOpen(notification)}
      onKeyDown={openOnKeyboardActivation}
    >
      <Flex
        className={styles.iconCircle}
        style={{ background: colors.bg, color: colors.fg }}
      >
        {typeConfig.icon}
      </Flex>
      <Flex className={styles.itemBody}>
        <Flex className={styles.itemHeader}>
          <Typography.Text strong={isUnread} ellipsis className={styles.senderName}>
            {getSenderName(notification.sender)}
          </Typography.Text>
          {contextLabel && (
            <Typography.Text type="secondary" className={styles.itemContext}>
              {contextLabel}
            </Typography.Text>
          )}
        </Flex>
        {isReaction && (
          <Typography.Paragraph
            className={styles.itemText}
            ellipsis={{ rows: 1, tooltip: notification.message }}
          >
            {notification.message}
          </Typography.Paragraph>
        )}
        {commentPreview ? (
          <Typography.Paragraph
            type={isReaction ? "secondary" : undefined}
            className={styles.itemText}
            ellipsis={{ rows: 2 }}
          >
            {isReaction ? `“${commentPreview}”` : commentPreview}
          </Typography.Paragraph>
        ) : (
          !isReaction && (
            <Typography.Text type="secondary" className={styles.itemText}>
              {notification.message}
            </Typography.Text>
          )
        )}
        <Typography.Text type="secondary" className={styles.itemTime}>
          {notification.created_at ? formatRelativeTime(notification.created_at) : ""}
        </Typography.Text>
      </Flex>
      {isUnread && <Flex className={styles.unreadDot} />}
    </Flex>
  );
}
